<template>
  <div class="bg-gray-1 p-3 mt-3">
    <div class="col-12">
      <ValidationObserver ref="Form">
        <icon-view
          :has-border="false"
          icon="fas fa-file-plus color-orange"
          icon-size="1rem"
          :title="isEdit ? 'Editar Servicio' : 'Añadir Servicio'"
          variant="custom-icon-view"
          class="mb-2 icon-view-color-text"
        />
        <div class="row py-2">
          <!-- El campo Fecha de Comienzo será utilizado a futuro
          con un permiso que pueda editar la fecha --->
          <template v-if="false">
            <div class="col-12 col-md-2 mt-2">
              <CustomDatePicker
                v-model="model.startDate"
                placeholder="DD/MM/YYYY"
                format="dd/MMM./yyyy"
                label="Fecha de Comienzo:"
                class="px-2 w-100"
                disabled
              />
            </div>
          </template>

          <div class="col-12 col-md-3 mt-2">
            <ValidationProvider
              name="Servicio"
              v-slot="{ errors }"
              mode="eager"
              rules="required"
              :custom-messages="{
                required: 'El servicio es requerido.',
              }"
            >
              <CustomDropDown
                v-model="model.service"
                label="Servicio:"
                fieldkey="id"
                fieldname="name"
                placeholder="Seleccione"
                :options="collections.services"
                :error="errors[0]"
                :custom-error-msg="errors[0]"
                class="px-2 mx-auto"
              />
            </ValidationProvider>
          </div>

          <template v-if="isOthers">
            <div class="col-12 col-md-3 mt-2">
              <ValidationProvider
                name="Servicio (Otros)"
                v-slot="{ errors }"
                mode="eager"
                rules="required"
              >
                <CustomInput
                  v-model="model.other"
                  label="Otros:"
                  class="px-2"
                  maxlength="3000"
                  :error="errors[0]"
                  custom-error-msg="El servicio otro es requerido."
                />
              </ValidationProvider>
            </div>
          </template>

          <div
            class="col-12 col-md-3 mt-2"
            v-if="isGeneralService || isAdditionalService"
          >
            <ValidationProvider
              name="Cantidad de meses"
              v-slot="{ errors }"
              mode="eager"
              rules="required|max_value:12|min_value:1"
              :custom-messages="{
                required: 'La cantidad de meses es requerida.',
                max_value: 'La cantidad de meses no debe ser mayor a 12.',
                min_value: 'La cantidad de meses no debe ser menor a 1.',
              }"
            >
              <CustomInput
                v-model="model.frequency"
                :disabled="frequencyIsDisabled"
                type="number"
                label="Cantidad de meses:"
                class="px-2 custom-input-frequency"
                :error="errors[0]"
                :custom-error-msg.sync="errors[0]"
              />
            </ValidationProvider>
          </div>

          <template v-if="canShowAdditionalFields">
            <div class="col-12 col-md-3 mt-2">
              <ValidationProvider
                name="Frecuencia (terapia)"
                v-slot="{ errors }"
                mode="eager"
                rules="required"
                :custom-messages="{
                  required: 'La frecuencia es requerida.',
                }"
              >
                <CustomDropDown
                  v-model="model.therapyFrequency"
                  label="Frecuencia:"
                  fieldkey="id"
                  fieldname="name"
                  placeholder="Seleccione"
                  :options="collections.frequencies"
                  :error="errors[0]"
                  :custom-error-msg="errors[0]"
                  class="px-2 mx-auto"
                />
              </ValidationProvider>
            </div>

            <div class="col-12 col-md-3 mt-2">
              <ValidationProvider
                name="Modalidad (terapia)"
                v-slot="{ errors }"
                mode="eager"
                rules="required"
                :custom-messages="{
                  required: 'La modalidad es requerida.',
                }"
              >
                <CustomDropDown
                  v-model="model.therapyServiceModality"
                  label="Modalidad:"
                  fieldkey="id"
                  fieldname="name"
                  placeholder="Seleccione"
                  :options="collections.modalities"
                  :error="errors[0]"
                  :custom-error-msg="errors[0]"
                  class="px-2 mx-auto"
                />
              </ValidationProvider>
            </div>

            <div class="col-12 col-md-3 mt-2">
              <ValidationProvider
                name="Duración (terapia)"
                v-slot="{ errors }"
                mode="eager"
                rules="required"
                :custom-messages="{
                  required: 'La duración es requerida.',
                }"
              >
                <CustomDropDown
                  v-model="model.therapyDuration"
                  label="Duración:"
                  fieldkey="id"
                  fieldname="name"
                  placeholder="Seleccione"
                  :options="collections.durations"
                  :error="errors[0]"
                  :custom-error-msg="errors[0]"
                  class="px-2 mx-auto"
                />
              </ValidationProvider>
            </div>
          </template>

          <div class="col-12 col-md-3 mt-2">
            <ValidationProvider
              name="Costo del servicio"
              v-slot="{ errors }"
              mode="eager"
              rules="required"
            >
              <CustomInputDefault
                v-model="model.cost"
                type="number"
                :label="getCostLabel"
                class="px-2"
                placeholder="0.00"
                :disabled="
                  (defaultTherapyCost > 0 && !canAlwaysTypeTherapyCost) ||
                    isCostReadOnly
                "
                :error="errors[0]"
                :custom-error-msg="getCostValidationLabel"
                prepend-icon="fas fa-dollar-sign"
                prepend-icon-type="success"
                prepend-icon-color="green"
              />
            </ValidationProvider>
          </div>
        </div>

        <div class="row" v-if="canShowComment">
          <div class="col-12 col-md-6 mt-2">
            <ValidationProvider
              v-slot="{ errors }"
              mode="eager"
              rules="required"
              name="comment"
            >
              <CustomTextArea
                v-model="model.comment"
                :label="'Comentario'"
                :placeholder="'Agrega un comentario'"
                :maxlength="200"
                :rows="3"
                :error="errors[0]"
                :custom-error-msg="errors[0]"
                required
                class="px-2"
              />
            </ValidationProvider>
          </div>
        </div>

        <div class="row" v-if="showExistingComment">
          <div class="col-12 col-md-6 mt-2">
            <div class="info-text-title">Comentario:</div>
            <div class="info-text">
              {{ formattedExistingComment }}
            </div>
            <div class="info-text" v-if="model.commentCreatedBy">
              {{ model.commentCreatedBy }}
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-9" />

          <div class="col-12 col-md-3 d-flex">
            <ActionButton
              variant="outline-danger m-1"
              i="fas fa-times-circle"
              responsive
              @click="cancel"
            >
              Cancelar
            </ActionButton>

            <template v-if="isEdit">
              <ActionButton
                v-if="!isSaveBtnDisabled"
                variant="success m-1"
                i="fas fa-save"
                responsive
                text="Guardar"
                @click="accept"
              />
            </template>

            <template v-else>
              <ActionButton
                v-if="!isSaveBtnDisabled"
                variant="success m-1"
                i="fas fa-file-plus"
                responsive
                text="Añadir"
                @click="accept"
              />
            </template>
          </div>
        </div>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import CustomDatePicker from 'custom-date-picker';
import CustomCallout from 'custom-callout';
import CustomDropDown from 'custom-drop-down';
import CustomInput from '@/components/common/CustomInput';
import { default as CustomInputDefault } from 'CustomInput';
import ActionButton from 'ActionButton';
import centralLevelServices from '@/utils/constants/tapRequest/centralLevelServices.js';
import centralLevelServicesCategories from '@/utils/constants/tapRequest/centralLevelServicesCategories';
import { tapRequestStatusesEnum } from '@/components/pei/ubication-tech-assistance/_types';
import alert from '@/mixin/alert.mixin';
import IconView from 'icon-view';
import CustomTextArea from '@/components/common/CustomTextArea';
import moment from 'moment';

export default {
  name: 'AddEditServiceForm',
  mixins: [alert],
  components: {
    CustomDatePicker,
    CustomCallout,
    CustomDropDown,
    CustomInput,
    CustomInputDefault,
    ActionButton,
    IconView,
    CustomTextArea,
  },
  props: {
    collections: {
      type: Object,
      default: () => ({
        services: [],
        itinerantServices: [],
        frequencies: [],
        modalities: [],
        durations: [],
      }),
    },
    value: {
      type: Object,
      default: () => ({
        id: 0,
        service: null,
        frequency: null,
        other: null,
        itinerantService: { id: false, name: 'No' },
        cost: null,
        costFromContract: null,
        subtotal: null,
        comment: null,
      }),
    },
    costReadonly: { type: Boolean, default: false },
    costByServices: { type: Array, default: () => [] },
    isAcceptBtnDisabled: { type: Boolean, default: false },
    isEdit: {
      type: Boolean,
      default: false,
    },
    statusId: { type: Number, default: 0 },
  },
  data() {
    return {
      model: {
        id: 0,
        cost: null,
        service: null,
        frequency: null,
        therapyFrequency: null,
        therapyServiceModality: null,
        therapyDuration: null,
        comment: null,
        commentCreatedAt: null,
        commentCreatedBy: null,
      },
      frequencyIsDisabled: false,
    };
  },
  watch: {
    'model.service': {
      handler() {
        this.frequencyIsDisabled = false;
        if (this.isAdditionalService) {
          this.frequencyIsDisabled = true;
          this.model.frequency = 1;
        }
        if (this.isTherapyService && !this.costReadonly) {
          this.getTherapyServiceCost();
        }
      },
      immediate: false,
    },
    isCostReadOnly(newValue, oldValue) {
      // if changed from readOnly to editable
      const canNowEdit = oldValue && !newValue;
      if (canNowEdit && this.isTherapyService) {
        this.getTherapyServiceCost();
      }
    },
  },
  computed: {
    isSaveBtnDisabled() {
      return (
        this.isTherapyService &&
        this.isAcceptBtnDisabled &&
        !this.defaultTherapyCost > 0
      );
    },
    isCostReadOnly() {
      return this.costReadonly && this.isTherapyService;
    },
    canShowComment() {
      const hasPermission = this.$store.getters.HasPermission(
        'academic.pei.ubicationtechassistance.edit.relatedservices.onprocessamendment'
      );
      const isProcessAmendment =
        this.statusId === tapRequestStatusesEnum['Proceso de Enmienda'];
      const isNewService = Number(this.model.id) === 0;

      const enable = false;

      return enable && hasPermission && isProcessAmendment && isNewService;
    },
    isOthers() {
      return (
        this.model.service &&
        this.model.service.id === centralLevelServices.Others
      );
    },
    canAlwaysTypeTherapyCost() {
      return this.$store.getters.HasPermission(
        'academic.pei.ubicationtechassistance.cost.edit'
      );
    },
    therapyServiceCost() {
      return this.costByServices.find(
        (x) => x?.serviceId === this.model.service?.id && !this.isCostReadOnly
      );
    },
    defaultTherapyCost() {
      if (!this.isTherapyService) return null;

      let cost = this.therapyServiceCost?.cost;
      this.model.cost = cost;

      return cost;
    },
    getCostLabel() {
      if (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.GeneralServices
      ) {
        return 'Costo mensual:';
      } else if (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.TherapyServices
      ) {
        return 'Tarifa por terapia:';
      } else if (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.AditionalServices
      ) {
        return 'Costo del servicio:';
      }

      return 'Costo:';
    },
    getCostValidationLabel() {
      if (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.GeneralServices
      ) {
        return 'El costo mensual es requerido.';
      } else if (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.TherapyServices
      ) {
        return 'La tarifa por terapia es requerida.';
      } else if (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.AditionalServices
      ) {
        return 'El costo del servicio es requerido.';
      }

      return 'El costo es requerido.';
    },
    isGeneralService() {
      return (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.GeneralServices
      );
    },
    isTherapyService() {
      return (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.TherapyServices
      );
    },
    isAdditionalService() {
      return (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.AditionalServices
      );
    },
    canShowAdditionalFields() {
      return (
        this.model.service?.id == centralLevelServices.ABATherapy ||
        this.model.service?.id === centralLevelServices.EducationalTherapy
      );
    },
    showExistingComment() {
      return (
        this.isEdit && this.model.comment && this.model.comment.trim() !== ''
      );
    },
    formattedExistingComment() {
      if (!this.showExistingComment) return '';

      const datePart = this.model.commentCreatedAt
        ? `${this.model.commentCreatedAt} - `
        : '';
      return `${datePart}${this.model.comment}`;
    },
  },
  methods: {
    cancel() {
      this.$emit('cancel');
    },
    async accept() {
      const valid = await this.$refs.Form.validate();

      if (valid) {
        const dto = { ...this.model };

        if (!this.isOthers) {
          dto.other = null;
        }

        // Si hay comentario y es un servicio nuevo, añadir fecha y usuario
        if (dto.comment && !this.isEdit && Number(this.model.id) === 0) {
          const now = moment();
          let formattedDate = now
            .locale('es-PR')
            .format('DD/MMM/YYYY hh:mm a')
            .toLowerCase();
          // Añadir punto después del mes (ej: nov. en lugar de nov)
          formattedDate = formattedDate.replace(/\/([a-z]{3})\//, '/$1./');
          dto.commentCreatedAt = formattedDate;
          dto.commentCreatedBy = null; // Se dejará null para que no muestre nada
        }

        this.$emit('accept', dto);
      } else {
        this.ShowToast(
          'Error',
          'Por favor, completar la información requerida.',
          'error'
        );
      }
    },

    getTherapyServiceCost() {
      const exists = this.therapyServiceCost !== undefined;
      if (exists) return;

      this.$emit('on-service-change', this.model.service);
    },
  },
  mounted() {
    this.model = { ...this.value };
  },
};
</script>

<style scoped lang="scss">
@use "sass:map";
@import '@/assets/scss/variables/_colors.scss';

::v-deep .input-group-prepend.success .input-group-text {
  background: map.get($colors, 'green') !important;
  color: map.get($colors, 'white') !important;
}

::v-deep .custom-input-frequency div input:disabled {
  background: #e2e2e2;
  cursor: not-allowed;
}

.icon-view-color-text {
  ::v-deep .color-gray-7 {
    color: #11291b !important;
  }
}

.info-text-title {
  color: map.get($colors, 'gray-6');
  font-size: 1rem;
  font-weight: normal;
}

.info-text {
  color: map.get($colors, 'black');
  font-weight: bold;
  font-size: 1rem;
  size: 1rem;
  overflow-wrap: break-word;
}
</style>
